import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import moment from 'moment';
import { GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core';
import { Injector } from '@angular/core';

import { OfferService } from '../../../../offer/services/offer.api.service';
import { DatacenterSelectionService } from '../../../services/datacenter-selection.service';
import { OrderBy, OrderByDirection } from '../../../utils/constant';
import { PaginatedResult } from '../../../models/paginated-result';
import { formatTextDimension } from '../../../utils/number.format';
import { FilterDateFormatType, DATE_FORMAT_PATTERN_DD_MM_YYYY,
	WEEKYEAR_FORMAT_PATTERN } from '../../../models/filter-formats';
import { CommonGridOptions } from '../../grid-options';
import { ColDefWithFields, ColumnType } from '../../column-type';
import { OfferItem } from '../../../../offer/models/offer';
import { Strategy } from '../strategy';
import { FilterModel } from '../../../models/ag-grid';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';

export class StrategyOffer implements Strategy<OfferItem> {
	offerService: OfferService;
	selectionService: DatacenterSelectionService;
	defaultOrderBy = new OrderBy<keyof OfferItem>('custOffRef', OrderByDirection.DESC);
	hasTotal = false;
	aggregateFields = [];

	constructor(injector: Injector) {
		this.offerService = injector.get<OfferService>(OfferService);
		this.selectionService = injector.get<DatacenterSelectionService>(DatacenterSelectionService);
	}

	setTotal(_api?: GridApi, _filter?: string): Subscription {
		throw new Error('Method not implemented.');
	}

	async getAllDataSelected(
		currentColumns: string[],
		filter: string | undefined,
		orderBy: OrderBy<keyof OfferItem>[],
	) {
		return this.getAllData(orderBy, currentColumns, filter)
			.then((offers: PaginatedResult<OfferItem>) => this.removeDeselectedRows(offers));
	}

	async getAllData(
		orderBy: OrderBy<keyof OfferItem>[],
		currentColumns: string[],
		filter: string | undefined
	) {
		return firstValueFrom(this.offerService.getOffers(
			<(keyof OfferItem)[]> currentColumns,
			filter,
			0,
			60_000,
			orderBy,
			false
		));
	}

	formatCellByType(columnDef: ColDefWithFields<OfferItem>, data: OfferItem) {
		const field = columnDef.field || '';
		const fields = columnDef.fields || [];
		const { columnType } = columnDef;
		switch (columnType) {
		case ColumnType.DIMENSION_COLUMN:
			return formatTextDimension(
				<string> data[<keyof OfferItem> fields[0]],
				<string> data[<keyof OfferItem> fields[1]],
				<string> data[<keyof OfferItem> fields[2]]
			);
		case ColumnType.ORDER_NUMBER_COLUMN:
		{
			let orderNumber = '';
			if (<string> data[<keyof OfferItem> fields[0]]) {
				orderNumber = <string> data[<keyof OfferItem> fields[0]];
			}
			if (<string> data[<keyof OfferItem> fields[0]] && <string> data[<keyof OfferItem> fields[1]]) {
				orderNumber += `/${ <string> data[<keyof OfferItem> fields[1]] }`;
			}
			return orderNumber;
		}
		case ColumnType.NUMBER_COLUMN:
		{
			const value = <number | undefined >data[<keyof OfferItem> field];
			return value === undefined ? '' : String(value);
		}
		case ColumnType.TEXT_COLUMN:
			return <string> data[<keyof OfferItem> field];
		case ColumnType.DATE_COLUMN:
			if (data[<keyof OfferItem> field]) {
				const dateformat = (<FilterDateFormatType> columnDef.cellRendererParams).outputFormat === 'week'
					? WEEKYEAR_FORMAT_PATTERN : DATE_FORMAT_PATTERN_DD_MM_YYYY;
				return moment.utc(<string> data[<keyof OfferItem> field]).format(dateformat);
			}
			return '';
		default:
			throw new Error('unknown filter type');
		}
	}

	getCustomGridOptions(defaultGridOptions: CommonGridOptions) {
		return new CommonGridOptions<OfferItem>({
			...defaultGridOptions
		});
	}

	getDataFromGrid(
		orderBy: OrderBy<keyof OfferItem>[],
		currentColumns: (keyof OfferItem)[],
		filter: string,
		params: IServerSideGetRowsParams<OfferItem>,
		rowsCountSubject: BehaviorSubject<number | undefined>
	): Subscription {
		const currentColumnsWithId = this.addIdColumn(currentColumns);
		const getOffers = this.offerService.getOffers(
			currentColumnsWithId,
			filter,
			params.request.startRow ?? 0,
			250,
			orderBy,
			false
		);
		return getOffers.subscribe({
			next: offers => {
				const lastRow = params.request.endRow === undefined || offers.count <= params.request.endRow
					? offers.count
					: -1;
				params.success({ rowData: offers.results, rowCount: lastRow });
				rowsCountSubject.next(offers.count);
			},
			error: (err: unknown) => {
				console.error('error requesting offer', err);
				rowsCountSubject.next(undefined);
				params.fail();
			}
		});
	}

	addIdColumn(currentColumns: (keyof OfferItem)[]) {
		if (!currentColumns.includes('id')) {
			currentColumns.push('id');
		}
		return currentColumns;
	}

	createAggregateCustomViewFilter(_view: UserDatacenterView): string {
		throw new Error('Method not implemented.');
	}

	createAggregateQuickSearchFilter(
		_currentSelectedView: UserDatacenterView,
		_api?: GridApi,
		_filterModel?: FilterModel
	): string {
		throw new Error('Method not implemented.');
	}

	private removeDeselectedRows(offers: PaginatedResult<OfferItem>) {
		return offers.results;
	}
}
